import {
  Component,
  ElementRef,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { MetaMasiva } from 'src/app/domain/model/meta-masiva/meta-masiva';
import {
  META_MASIVA_META_MES_MAX_LENGTH,
  MESES_META
} from 'src/app/common/constants-ejb';

/** Valor digitado en la celda Meta Mes junto con la fila que se confirma. */
export interface MetaMesConfirmada {
  meta: MetaMasiva;
  valor: string;
}

/**
 * Tabla (dumb component) del listado "Listado Metas Mes" (HU1290096).
 *
 * <p>Columnas del legacy (ListarMetasAnioViewImpl): Mes (en letras), Oficina, Tipo Asesor y Meta Mes.
 * Solo la columna Meta Mes es editable en la celda; la validación y el guardado viven en el facade: el
 * componente solo recoge el valor y emite {@link confirmar}. Una Meta Mes nula se muestra "0". La
 * paginación es manual y vive en el container (nunca `[paginator]="true"`).</p>
 */
@Component({
  selector: 'app-tabla-metas',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TooltipModule
  ],
  template: `
    <p-table
      styleClass="p-datatable-sm p-datatable-gridlines"
      [value]="items()"
      [paginator]="false"
      [responsiveLayout]="'scroll'"
      [loading]="loading()"
      [scrollable]="true"
      scrollHeight="400px"
      dataKey="nmconsecutivo"
    >
      <ng-template pTemplate="header">
        <tr>
          <th>Mes</th>
          <th>Oficina</th>
          <th>Tipo Asesor</th>
          <th class="text-center" style="width: 14rem">Meta Mes</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-item>
        <tr>
          <td>{{ nombreMes(item.mes) }}</td>
          <td>{{ item.oficina }}</td>
          <td>{{ item.tipoAsesor }}</td>
          <td class="text-center">
            @if (editandoId() === item.nmconsecutivo) {
              <div class="flex items-center justify-center gap-1 min-w-0">
                <input
                  pInputText
                  class="meta-mes-editor flex-1 min-w-0 w-full text-center"
                  inputmode="decimal"
                  autocomplete="off"
                  aria-required="true"
                  [maxlength]="metaMaxLength"
                  [attr.aria-label]="'Meta Mes de ' + nombreMes(item.mes)"
                  [ngModel]="valorEdicion()"
                  (ngModelChange)="valorEdicion.set($event)"
                  (keyup.enter)="confirmarEdicion(item)"
                  (keyup.escape)="cancelar.emit()"
                />
                <button
                  pButton
                  type="button"
                  icon="pi pi-check"
                  class="p-button-rounded p-button-text p-button-sm shrink-0 min-w-[44px] min-h-[44px]"
                  pTooltip="Confirmar"
                  aria-label="Confirmar Meta Mes"
                  (click)="confirmarEdicion(item)"
                ></button>
                <button
                  pButton
                  type="button"
                  icon="pi pi-times"
                  class="p-button-rounded p-button-text p-button-danger p-button-sm shrink-0 min-w-[44px] min-h-[44px]"
                  pTooltip="Cancelar"
                  aria-label="Cancelar edición"
                  (click)="cancelar.emit()"
                ></button>
              </div>
            } @else {
              <button
                type="button"
                class="meta-mes-valor inline-flex items-center justify-center gap-2 min-w-[44px] min-h-[44px] px-2 rounded cursor-pointer bg-transparent text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                pTooltip="Editar Meta Mes"
                [attr.aria-label]="'Editar Meta Mes de ' + nombreMes(item.mes)"
                (click)="iniciarEdicion(item)"
              >
                <span>{{ textoMeta(item.metaMes) }}</span>
                <i class="pi pi-pencil text-xs" aria-hidden="true"></i>
              </button>
            }
          </td>
        </tr>
      </ng-template>
    </p-table>
  `
})
export class TablaMetasComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly items = input.required<MetaMasiva[]>();
  readonly loading = input(false);
  /** Consecutivo de la fila en edición (lo decide el facade). */
  readonly editandoId = input<number | null>(null);

  readonly editar = output<MetaMasiva>();
  readonly confirmar = output<MetaMesConfirmada>();
  readonly cancelar = output<void>();

  protected readonly metaMaxLength = META_MASIVA_META_MES_MAX_LENGTH;
  protected readonly valorEdicion = signal('');

  /** Mes en letras (ENERO...DICIEMBRE); "-" si el código no existe. */
  nombreMes(mes: number | null | undefined): string {
    return MESES_META.find((m) => m.codigo === mes)?.descripcion ?? '-';
  }

  /** Meta Mes para mostrar; "0" cuando viene nula (Escenario 4). */
  textoMeta(valor: number | null | undefined): string {
    return String(valor ?? 0);
  }

  iniciarEdicion(meta: MetaMasiva): void {
    this.valorEdicion.set(this.textoMeta(meta.metaMes));
    this.editar.emit(meta);
    // El editor aparece en el siguiente ciclo de detección; se le da el foco para escribir de inmediato.
    setTimeout(() =>
      this.host.nativeElement
        .querySelector<HTMLInputElement>('input.meta-mes-editor')
        ?.focus()
    );
  }

  confirmarEdicion(meta: MetaMasiva): void {
    this.confirmar.emit({ meta, valor: this.valorEdicion() });
  }
}
